import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Search } from 'lucide-react'
import { canManagePromoCodes } from '@manaro/contracts'
import { useSession } from '@/features/auth/use-session.ts'
import { CreatePromoDialog } from '@/features/promo-codes/components/create-promo-dialog.tsx'
import { PromoCodesList } from '@/features/promo-codes/components/promo-codes-list.tsx'
import { matchesSearch, splitByStatus } from '@/features/promo-codes/form.ts'
import { promoCodePath } from '@/features/promo-codes/labels.ts'
import { usePromoCodes } from '@/features/promo-codes/use-promo-codes.ts'
import { ApiError } from '@/shared/api/client.ts'
import { Button } from '@/shared/ui/button.tsx'
import { Card } from '@/shared/ui/card.tsx'
import { Chip } from '@/shared/ui/chip.tsx'
import { Alert, EmptyState, ErrorState, Skeleton } from '@/shared/ui/states.tsx'

type View = 'active' | 'inactive' | 'all'

export function PromoCodesPage() {
  const navigate = useNavigate()
  const access = useSession().data?.user.access
  const mayManage = access !== undefined && canManagePromoCodes(access)
  const list = usePromoCodes()
  const [view, setView] = useState<View>('active')
  const [search, setSearch] = useState('')
  const [creating, setCreating] = useState(false)

  const rows = list.data?.rows ?? []
  const groups = useMemo(() => splitByStatus(rows), [rows])
  const shown = (view === 'all' ? rows : groups[view]).filter((promo) => matchesSearch(promo, search))

  const views: Array<{ value: View; label: string; count: number }> = [
    { value: 'active', label: 'Действуют', count: groups.active.length },
    { value: 'inactive', label: 'Не действуют', count: groups.inactive.length },
    { value: 'all', label: 'Все', count: rows.length },
  ]

  return (
    <>
      <header className="mb-6 flex flex-wrap items-end gap-x-5 gap-y-3.5">
        <div className="min-w-0 flex-1 basis-[300px]">
          <h1 className="mb-2 font-display text-[25px] leading-none font-semibold tracking-[-0.02em] md:text-[32px]">
            Промокоды
          </h1>
          <p className="text-[15px] text-ink-muted text-balance-pretty">
            Скидка на оплату тарифа или бесплатные месяцы. Салон вводит код в CRM при регистрации, при создании салона или
            на странице тарифов — один раз.
          </p>
        </div>
        {mayManage && list.data?.available !== false && (
          <Button size="sm" icon={<Plus className="size-4" />} onClick={() => setCreating(true)} className="max-md:min-h-11 max-md:w-full">
            Создать промокод
          </Button>
        )}
      </header>

      {list.data && !list.data.available ? (
        <Alert>
          Бэкенд Manaro не подключён: в настройках сервера не заданы <code>MANARO_API_URL</code> и{' '}
          <code>MANARO_STATS_TOKEN</code>.
        </Alert>
      ) : (
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-2 max-md:[&>button]:min-h-11">
            {views.map((option) => (
              <Chip
                key={option.value}
                label={option.label}
                count={list.data ? option.count : undefined}
                active={view === option.value}
                onClick={() => setView(option.value)}
              />
            ))}
          </div>

          <Card className="overflow-hidden">
            <label className="flex items-center gap-2.5 border-b border-line-soft px-4 py-3.5 md:px-5">
              <Search className="size-4 shrink-0 text-ink-faint" aria-hidden />
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Поиск по коду или заметке…"
                className="w-full bg-transparent text-[16px] outline-none placeholder:text-ink-faint md:text-[14.5px]"
              />
            </label>

            {list.isPending ? (
              <div className="flex flex-col gap-3 p-5">
                <Skeleton className="h-10" />
                <Skeleton className="h-10" />
                <Skeleton className="h-10" />
              </div>
            ) : list.error ? (
              <ErrorState
                message={list.error instanceof ApiError ? list.error.message : 'Не удалось загрузить промокоды.'}
                onRetry={() => void list.refetch()}
              />
            ) : shown.length === 0 ? (
              <EmptyState
                title={rows.length === 0 ? 'Промокодов пока нет' : 'Ничего не нашли'}
                description={
                  rows.length === 0
                    ? mayManage
                      ? 'Создайте первый: скидку для рассылки или пару бесплатных месяцев для партнёров.'
                      : 'Их создаёт владелец сервиса.'
                    : 'Попробуйте другой запрос или вкладку.'
                }
              />
            ) : (
              <PromoCodesList rows={shown} />
            )}
          </Card>
        </div>
      )}

      {creating && (
        <CreatePromoDialog onClose={() => setCreating(false)} onDone={(promo) => navigate(promoCodePath(promo.id))} />
      )}
    </>
  )
}
